// Wiki-specific Markdown renderer — optimized for long-form knowledge articles.
// Handles: headings with anchor IDs, tables, code blocks, bold/italic, lists,
// blockquotes, [[backlinks]], horizontal rules, warning/critical callouts.
// Designed for beautiful reading, not chat messages.

import DOMPurify from 'dompurify'

interface WikiMarkdownRendererProps {
  content: string
  onBacklinkClick?: (slug: string) => void
}

export default function WikiMarkdownRenderer({ content, onBacklinkClick }: WikiMarkdownRendererProps) {
  const html = renderWikiMarkdown(content, onBacklinkClick)
  const clean = DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', 'rel', 'id'],
    ADD_TAGS: ['style'],
  })
  return (
    <div
      className="wiki-article-content"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  )
}

// Extract headings for table of contents
export function extractHeadings(content: string): { level: number; text: string; id: string }[] {
  const headings: { level: number; text: string; id: string }[] = []
  const lines = content.split('\n')
  let inCodeBlock = false

  for (const line of lines) {
    if (line.startsWith('```')) { inCodeBlock = !inCodeBlock; continue }
    if (inCodeBlock) continue

    const match = line.match(/^(#{1,4})\s+(.+)/)
    if (match) {
      const level = match[1].length
      const text = match[2].replace(/\*\*/g, '').replace(/\*/g, '').trim()
      const id = slugify(text)
      headings.push({ level, text, id })
    }
  }
  return headings
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

function renderWikiMarkdown(text: string, onBacklinkClick?: (slug: string) => void): string {
  // Handle code blocks first
  const codeBlocks: string[] = []
  let processed = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_m, lang, code) => {
    const idx = codeBlocks.length
    const langLabel = lang
      ? `<div class="wiki-code-lang">${esc(lang)}</div>`
      : ''
    codeBlocks.push(
      `<div class="wiki-code-block">${langLabel}<pre><code>${esc(code.trim())}</code></pre></div>`
    )
    return `\x00CB${idx}\x00`
  })

  // Process blocks (split on double newlines)
  const blocks = processed.split(/\n\n+/)

  return blocks.map(block => {
    // Restore code blocks
    if (block.match(/\x00CB\d+\x00/)) {
      return block.replace(/\x00CB(\d+)\x00/g, (_m, idx) => codeBlocks[parseInt(idx)] || '')
    }

    const trimmed = block.trim()

    // Horizontal rule
    if (trimmed.match(/^---+$/m)) return '<hr class="wiki-hr">'

    // Headers with anchor IDs
    const headerMatch = trimmed.match(/^(#{1,4})\s+(.+)/)
    if (headerMatch) {
      const level = headerMatch[1].length
      const text = headerMatch[2]
      const id = slugify(text.replace(/\*\*/g, '').replace(/\*/g, ''))
      const tag = `h${level + 1}` // h1 in md → h2 in html (h1 reserved for page title)
      return `<${tag} id="${id}" class="wiki-heading wiki-h${level}">${wikiInline(text, onBacklinkClick)}</${tag}>`
    }

    // Callout blocks (> ⚠️ or > 🔴 or > 💡)
    if (trimmed.startsWith('> ')) {
      const inner = trimmed.split('\n').map(l => l.replace(/^>\s?/, '')).join('\n')
      const isWarning = inner.includes('⚠️') || inner.toLowerCase().includes('warning') || inner.toLowerCase().includes('critical')
      const isCritical = inner.includes('🔴') || inner.toLowerCase().startsWith('critical')
      const cls = isCritical ? 'wiki-callout-critical' : isWarning ? 'wiki-callout-warning' : 'wiki-blockquote'
      const lines = inner.split('\n').map(l => wikiInline(l, onBacklinkClick)).join('<br>')
      return `<blockquote class="${cls}">${lines}</blockquote>`
    }

    // Unordered list (handle nested)
    if (trimmed.match(/^[\s]*[-*] /m)) {
      return renderList(trimmed, false, onBacklinkClick)
    }

    // Ordered list
    if (trimmed.match(/^[\s]*\d+\. /m)) {
      return renderList(trimmed, true, onBacklinkClick)
    }

    // Table
    if (trimmed.includes('|') && trimmed.includes('\n')) {
      const rows = trimmed.split('\n').filter(r => r.trim().startsWith('|'))
      if (rows.length >= 2) {
        return renderTable(rows, onBacklinkClick)
      }
    }

    // Paragraph
    const lines = trimmed.split('\n').map(l => wikiInline(l, onBacklinkClick)).join('<br>')
    if (!lines.trim()) return ''
    return `<p class="wiki-paragraph">${lines}</p>`
  }).join('\n')
}

function renderList(block: string, ordered: boolean, onBacklinkClick?: (slug: string) => void): string {
  const lines = block.split('\n').filter(l => l.trim())
  const items: string[] = []

  for (const line of lines) {
    const indent = line.match(/^(\s*)/)?.[1]?.length || 0
    const text = line.replace(/^\s*[-*]\s+/, '').replace(/^\s*\d+\.\s+/, '')
    const cls = indent >= 2 ? 'wiki-list-nested' : ''
    items.push(`<li class="${cls}">${wikiInline(text, onBacklinkClick)}</li>`)
  }

  const tag = ordered ? 'ol' : 'ul'
  return `<${tag} class="wiki-list">${items.join('\n')}</${tag}>`
}

function renderTable(rows: string[], onBacklinkClick?: (slug: string) => void): string {
  const headerCells = rows[0].split('|').filter(Boolean).map(c => c.trim())
  const isSeparator = (r: string) => r.replace(/[|\s\-:]/g, '') === ''
  const dataRows = rows.filter((r, i) => i > 0 && !isSeparator(r))

  const ths = headerCells.map(c =>
    `<th class="wiki-th">${wikiInline(c, onBacklinkClick)}</th>`
  ).join('')

  const trs = dataRows.map(r => {
    const cells = r.split('|').filter(Boolean).map(c => c.trim())
    return `<tr>${cells.map(c =>
      `<td class="wiki-td">${wikiInline(c, onBacklinkClick)}</td>`
    ).join('')}</tr>`
  }).join('')

  return `<div class="wiki-table-wrapper"><table class="wiki-table"><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table></div>`
}

function wikiInline(text: string, onBacklinkClick?: (slug: string) => void): string {
  let s = esc(text)

  // [[Backlinks]] → clickable internal links
  s = s.replace(/\[\[(.+?)\]\]/g, (_m, target) => {
    const slug = target.replace(/\s+/g, '-')
    if (onBacklinkClick) {
      return `<a href="/wiki/${encodeURIComponent(slug)}" class="wiki-backlink" data-wiki-link="${esc(slug)}">${esc(target)}</a>`
    }
    return `<a href="/wiki/${encodeURIComponent(slug)}" class="wiki-backlink">${esc(target)}</a>`
  })

  // Bold
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong class="wiki-bold">$1</strong>')
  // Italic
  s = s.replace(/\*(.+?)\*/g, '<em class="wiki-italic">$1</em>')
  // Inline code
  s = s.replace(/`(.+?)`/g, '<code class="wiki-inline-code">$1</code>')
  // Links
  s = s.replace(/\[(.+?)\]\((.+?)\)/g, (_m, label, url) => {
    const safeUrl = isSafeUrl(url) ? url : '#'
    return `<a href="${safeUrl}" class="wiki-link" target="_blank" rel="noopener noreferrer">${label}</a>`
  })
  // Hashtags (but not inside words or HTML attributes)
  s = s.replace(/(^|\s)(#[\w/\-]+)/g, '$1<span class="wiki-tag">$2</span>')

  return s
}

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim().toLowerCase()
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('mailto:')) return true
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('./') || trimmed.startsWith('../')) return true
  return !trimmed.includes(':')
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
